import React, { FC, useEffect, useState } from 'react'
import { Button, Col, Form, Row } from 'react-bootstrap'
import classnames from 'classnames'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { navigate } from 'hookrouter'

import {
  Action,
  NewCompanyFormInputProps,
  NewCompanyFormValidationErrors,
  RevealingButtonProps,
} from 'types'
import { APP_ROUTES } from 'pages/AppRouter'
import { convertCentsToUSD } from 'utils/convertCentsToUSD'
import { createCompany, fetchStripeProducts } from 'state/async_actions'
// import { ErrorsDisplay } from '../ErrorsDisplay'
import { ProductCard } from '../ProductCard'
import { StripeCheckoutForm } from '../StripeCheckoutForm'
import { useAppContext } from 'state/context'
import { useForm } from 'hooks/useForm'

export const SubmitFormButton: FC = () => {
  return (
    <Button variant="primary" value="Create new Company" type="submit">
      Authorize Payments and Create New Company
    </Button>
  )
}

export const RevealingButton: FC<RevealingButtonProps> = ({
  name,
  text,
  onClick,
}) => {
  return (
    <Button type="button" onClick={onClick} name={name}>
      {text}
    </Button>
  )
}

export const validateForm = (
  inputs: NewCompanyFormInputProps,
): NewCompanyFormValidationErrors => {
  const errors: NewCompanyFormValidationErrors = {}
  if (!inputs.name) {
    errors.name = 'Company name must have a value'
  }
  if (!inputs.product) {
    errors.product = 'A product must be selected'
  }
  if (!inputs.paymentMethodId) {
    errors.paymentMethodId = 'Credit card is invalid'
  }
  return errors
}

export const handleFormSubmit = (dispatch: React.Dispatch<Action>) => (
  inputs: NewCompanyFormInputProps,
) => {
  createCompany(dispatch, inputs)
  navigate(APP_ROUTES.root)
}

export const NewCompanyForm = () => {
  const [{ stripeProducts }, dispatch] = useAppContext()

  const [
    { name },
    handleInputChange,
    handleSubmit,
    validated,
    includeFormManagedValue,
    validationErrors,
  ] = useForm<NewCompanyFormInputProps>(
    handleFormSubmit(dispatch),
    validateForm,
  )

  useEffect(() => {
    fetchStripeProducts(dispatch)
  }, [dispatch])

  const [selectedPrice, setSelectedPrice] = useState<string>('')
  const [paymentMethodId, setPaymentMethodId] = useState('')

  const handlePriceSelection = (priceId: string) => {
    setSelectedPrice(priceId)
    includeFormManagedValue({ product: priceId })
  }

  const handlePaymentMethodId = (id: string) => {
    setPaymentMethodId(id)
    includeFormManagedValue({ paymentMethodId: id })
  }

  type NewCompanyFormRenderState = {
    components: {
      plan: boolean
      stripe: boolean
    }
    revealingButtons: {
      plan: boolean
      stripe: boolean
      submit: boolean
    }
  }

  const [elementsToRender, setElementsToRender] = useState({
    components: {
      plan: false,
      stripe: false,
    },
    revealingButtons: {
      plan: true,
      stripe: false,
      submit: false,
    },
  })

  const renderPlanSelection = () => {
    setElementsToRender(
      (prevState: NewCompanyFormRenderState): NewCompanyFormRenderState => {
        return {
          components: {
            plan: true,
            stripe: prevState.components.stripe,
          },
          revealingButtons: {
            plan: false,
            stripe: true,
            submit: prevState.revealingButtons.submit,
          },
        }
      },
    )
  }

  const renderStipeElements = () => {
    if (selectedPrice !== '') {
      setElementsToRender(
        (prevState: NewCompanyFormRenderState): NewCompanyFormRenderState => {
          return {
            components: {
              plan: prevState.components.plan,
              stripe: true,
            },
            revealingButtons: {
              plan: prevState.revealingButtons.plan,
              stripe: false,
              submit: prevState.revealingButtons.submit,
            },
          }
        },
      )
    }
  }

  const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY as string
  const stripePromise = loadStripe(stripeKey)

  return (
    <>
      <Row>
        <Col>
          <h1>Create a new Company</h1>
          {/* <ErrorsDisplay /> */}
        </Col>
      </Row>
      <Row>
        <Col>
          <Form
            noValidate
            validated={validated}
            onSubmit={handleSubmit}
            className="new-company-form">
            <Form.Group controlId="formBasicName">
              <Form.Label>Name</Form.Label>
              <Form.Control
                required
                onChange={handleInputChange}
                value={name || ''}
                name="name"
                type="text"
                placeholder="Enter Company Name"
              />
              {validationErrors.name && (
                <div className="text-danger">{validationErrors.name}</div>
              )}

              {elementsToRender.revealingButtons.plan && (
                <RevealingButton
                  name="renderPlanSelection"
                  onClick={renderPlanSelection}
                  text="Select Payment Plan"
                />
              )}
            </Form.Group>

            {elementsToRender.components.plan && (
              <Form.Group>
                <Row className={classnames('product-card-container')}>
                  {stripeProducts.length ? (
                    stripeProducts.map(product => (
                      <Col
                        className={classnames('product-card-column')}
                        key={`${product.priceId}-col`}
                        sm={12}
                        lg={Math.floor(12 / stripeProducts.length)}>
                        <ProductCard
                          active={selectedPrice === product.priceId}
                          amountInDollars={convertCentsToUSD(product.amount)}
                          interval={product.interval}
                          key={product.priceId}
                          onClick={handlePriceSelection}
                          priceId={product.priceId}
                          title={product.productName}
                        />
                      </Col>
                    ))
                  ) : (
                    <div>Loading pricing information... please wait</div>
                  )}
                </Row>
                {validationErrors.product && (
                  <div className="text-danger">{validationErrors.product}</div>
                )}

                {elementsToRender.revealingButtons.stripe && (
                  <RevealingButton
                    name="renderStripeElements"
                    onClick={renderStipeElements}
                    text={selectedPrice === '' ? 'Select plan' : 'Confirm Plan'}
                  />
                )}
              </Form.Group>
            )}

            {elementsToRender.components.stripe && stripePromise && (
              <Form.Group>
                <Elements stripe={stripePromise}>
                  <StripeCheckoutForm
                    handlePaymentMethodId={handlePaymentMethodId}
                    paymentMethodId={paymentMethodId}
                  />
                </Elements>
                {validationErrors.paymentMethodId && (
                  <div className="text-danger">
                    {validationErrors.paymentMethodId}
                  </div>
                )}
              </Form.Group>
            )}
            {paymentMethodId && <SubmitFormButton />}
          </Form>
        </Col>
      </Row>
    </>
  )
}
